"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getClusterOfBlockchain = getClusterOfBlockchain;
exports.getClusterInfo = getClusterInfo;
exports.awaitGetAnchoringTransactionForBlockRid = awaitGetAnchoringTransactionForBlockRid;
exports.getAnchoringTransactionForBlockRid = getAnchoringTransactionForBlockRid;
exports.convertToAnchoringTransaction = convertToAnchoringTransaction;
exports.calculateBlockRID = calculateBlockRID;
exports.fetchAndVerifyTransaction = fetchAndVerifyTransaction;
exports.composeProofTransactionObject = composeProofTransactionObject;
const buffer_1 = require("buffer");
const errors_1 = require("../blockchainClient/errors");
const utils_1 = require("../blockchainClient/utils");
const anchoringTransaction_1 = require("../blockchainClient/validation/anchoringTransaction");
const encryption_1 = require("../encryption/encryption");
const formatter_1 = require("../formatter");
const gtv_1 = require("../gtv");
const gtx_1 = require("../gtx/gtx");
const serialization_1 = require("../gtx/serialization");
const error_1 = require("./error");
function getClusterOfBlockchain(client, blockchainRid) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const clusterName = yield client.query({
                name: "cm_get_blockchain_cluster",
                args: { brid: blockchainRid },
            });
            return clusterName;
        }
        catch (error) {
            throw new error_1.SystemChainException(error.message);
        }
    });
}
function getClusterInfo(client, name) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const clusterInfo = yield client.query({
                name: "cm_get_cluster_info",
                args: { name },
            });
            return clusterInfo;
        }
        catch (error) {
            throw new error_1.SystemChainException(error.message);
        }
    });
}
function awaitGetAnchoringTransactionForBlockRid(client, blockchainRid, blockRid, systemAnchoringStatusPolling) {
    return __awaiter(this, void 0, void 0, function* () {
        let anchoringTransactionResponse = null;
        for (let i = 0; i < systemAnchoringStatusPolling.count; i++) {
            anchoringTransactionResponse = yield getAnchoringTransactionForBlockRid(client, blockchainRid, blockRid);
            if (anchoringTransactionResponse) {
                const anchoringTransactionValidaiton = anchoringTransaction_1.AnchoringTransactionSchema.safeParse(anchoringTransactionResponse);
                if (anchoringTransactionValidaiton.success) {
                    return anchoringTransactionValidaiton.data;
                }
                else if (!anchoringTransactionValidaiton.success) {
                    throw new errors_1.TxRejectedError("Invalid anchoring transaction format");
                }
            }
            yield (0, utils_1.sleep)(systemAnchoringStatusPolling.interval);
        }
        return anchoringTransactionResponse;
    });
}
function getAnchoringTransactionForBlockRid(client, blockchainRid, blockRid) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const anchoringTxForBlockRid = yield client.query({
                name: "get_anchoring_transaction_for_block_rid",
                args: { blockchain_rid: blockchainRid, block_rid: blockRid },
            });
            if (!anchoringTxForBlockRid)
                return null;
            return convertToAnchoringTransaction(anchoringTxForBlockRid);
        }
        catch (error) {
            throw new error_1.SystemChainException(error.message);
        }
    });
}
function convertToAnchoringTransaction(responseTx) {
    const { tx_rid, tx_data, tx_op_index } = responseTx;
    return {
        txRid: tx_rid,
        txData: tx_data,
        txOpIndex: tx_op_index,
    };
}
function calculateBlockRID(decodedTxProof, merkleHashVersion) {
    const sourceBlockHeader = decodedTxProof.blockHeader;
    if (!sourceBlockHeader) {
        throw new Error("Failed to get blockHeader from confirmation proof");
    }
    const decodeSourceBlockRid = (0, serialization_1.decodeValue)(sourceBlockHeader);
    return (0, gtv_1.gtvHash)(decodeSourceBlockRid, merkleHashVersion);
}
// fetch tx from txRID and verifies with secp256k1.ecdsaVerify that txRID and signer creates signatures that are on the blockchain transaction
function fetchAndVerifyTransaction(sourceClient, txToProveRID, proofHash, txToProveSigners) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const rawTx = yield sourceClient.getTransaction(txToProveRID);
        const txGtv = (0, serialization_1.decodeValue)(rawTx);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const fetchedTxHash = (0, gtv_1.gtvHash)(txGtv, sourceClient.config.merkleHashVersion);
        if (buffer_1.Buffer.compare(fetchedTxHash, proofHash)) {
            // We received another hash for tx RID than what was included in proof
            // Possibly rouge or faulty node(s). Anyway, we need to give up.
            throw new error_1.MissingTransactionProof(proofHash, fetchedTxHash);
        }
        const fetchedTx = (0, gtx_1.rawGtvToGtx)(txGtv);
        if (txToProveSigners.length != ((_a = fetchedTx.signatures) === null || _a === void 0 ? void 0 : _a.length)) {
            throw new error_1.DifferentNumberOfSignersException((_c = (_b = fetchedTx.signatures) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0, txToProveSigners.length);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const txRID = (0, gtx_1.getDigestToSign)(fetchedTx, sourceClient.config.merkleHashVersion);
        if (buffer_1.Buffer.compare(txRID, (0, formatter_1.ensureBuffer)(txToProveRID))) {
            throw new error_1.ProofRidException();
        }
        for (const signer of txToProveSigners) {
            let hasSignature = false;
            for (const signature of fetchedTx.signatures) {
                // verify that txRID (hash of gtxBody) signed by signers equals the signatures from network
                if ((0, encryption_1.checkDigestSignature)(txRID, signer, signature)) {
                    hasSignature = true;
                    break;
                }
            }
            if (!hasSignature)
                throw new error_1.SignatureException(signer);
        }
        return { verifiedTx: fetchedTx, verifiedTxHash: fetchedTxHash };
    });
}
function composeProofTransactionObject(sourceBlockchainRid, verifiedTxHash, txProof, iccfTxSigners, anchoringTx, anchoringProof, isNetwork = true) {
    let operationArgs;
    operationArgs = {
        sourceBlockchainRid: (0, formatter_1.ensureBuffer)(sourceBlockchainRid),
        transactionHash: verifiedTxHash,
        transactionProof: (0, serialization_1.encodeValue)(txProof),
    };
    if (isNetwork) {
        operationArgs = Object.assign(Object.assign({}, operationArgs), { transactionData: anchoringTx === null || anchoringTx === void 0 ? void 0 : anchoringTx.txData, transactionIndex: anchoringTx === null || anchoringTx === void 0 ? void 0 : anchoringTx.txOpIndex, anchoringProof: anchoringProof && (0, serialization_1.encodeValue)(anchoringProof) });
    }
    return {
        operations: [
            {
                name: "iccf_proof",
                args: Object.values(operationArgs),
            },
        ],
        signers: iccfTxSigners,
    };
}
//# sourceMappingURL=utils.js.map